export type ConnectableIntegrationScenario =
  | "Store Ops"
  | "Advertising"
  | "Marketing"
  | "Email Marketing"
  | "Analytics"
  | "SEO"
  | "Social Media"
  | "Customers"
  | "Finance"
  | "Team Collaboration";

export type IntegrationBrowseScenario =
  | ConnectableIntegrationScenario
  | "Customers"
  | "Finance"
  | "Team Collaboration"
  | "MCP";

export type IntegrationBrowseDisplayScenario = Exclude<
  IntegrationBrowseScenario,
  "Email Marketing"
>;

export interface IntegrationCatalogGroup {
  category: string;
  tools: string[];
}

export const MCP_TOOL_NAME = "Custom MCP";

/**
 * Integrations that must collect credentials before starting a connection.
 * Settings and onboarding share this contract so neither entry point can
 * accidentally call the connection API without the required input.
 */
export const MANUAL_CREDENTIAL_INTEGRATION_TOOLS: readonly string[] = [
  "Ahrefs",
  "Omnisend",
  "Figma",
  "Okendo",
  "GoAffPro",
  "UpPromote",
  "Loox",
  "TrustWILL",
  "NoxInfluencer",
  "PrestaShop",
  "Attribuly",
];

const MANUAL_CREDENTIAL_INTEGRATION_TOOL_SET = new Set(
  MANUAL_CREDENTIAL_INTEGRATION_TOOLS,
);

export const requiresManualCredentialInput = (tool?: string | null): boolean =>
  Boolean(tool && MANUAL_CREDENTIAL_INTEGRATION_TOOL_SET.has(tool));

/**
 * Provider keys that currently have a real connection path in Settings.
 * Catalog entries that are not in this allowlist remain visible as placeholders.
 */
export const SUPPORTED_INTEGRATION_APPS: readonly string[] = [
  "custom_mcp",
  "shopify",
  "bigcommerce",
  "google_ads",
  "google_analytics",
  "tiktok_ads",
  "google_search_console",
  "semrush",
  "woocommerce",
  "magento",
  "prestashop",
  "klaviyo",
  "mailchimp",
  "judgeme",
  "feishu",
  "dingtalk",
  "ahrefs",
  "omnisend",
  "okendo",
  "goaffpro",
  "uppromote",
  "loox",
  "trustwill",
  "noxinfluencer",
  "wix",
  "meta_ads",
  "facebook_page",
  "instagram",
  "pinterest_ads",
  "x_ads",
  "youtube",
  "reddit",
  "attribuly",
  "figma",
];

/**
 * Display-tool key to the provider key expected by the connection APIs.
 * Entries can exist here even when the tool is still a visible placeholder.
 */
export const INTEGRATION_TOOL_TO_PROVIDER: Readonly<Record<string, string>> = {
  custom_mcp: "custom-mcp",
  shopify: "shopify",
  bigcommerce: "bigcommerce",
  google_ads: "google-ads",
  google_analytics: "google-analytics",
  tiktok_ads: "tiktok-ads",
  google_search_console: "google-search-console",
  semrush: "semrush",
  slack: "slack",
  woocommerce: "woocommerce",
  magento: "magento",
  prestashop: "prestashop",
  aliexpress: "aliexpress",
  klaviyo: "klaviyo",
  mailchimp: "mailchimp",
  feishu: "feishu",
  dingtalk: "dingtalk",
  similarweb: "similarweb",
  ahrefs: "ahrefs",
  omnisend: "omnisend",
  okendo: "okendo",
  goaffpro: "goaffpro",
  uppromote: "uppromote",
  loox: "loox",
  trustwill: "trustwill",
  noxinfluencer: "noxinfluencer",
  insider_one: "insider-one",
  emarsys: "emarsys",
  "judge.me": "judgeme",
  judgeme: "judgeme",
  wix: "wix",
  meta_ads: "meta-ads",
  facebook_page: "facebook-page",
  instagram: "instagram",
  linkedin_ads: "linkedin-ads",
  pinterest_ads: "pinterest-ads",
  x_ads: "x-ads",
  youtube_ads: "youtube",
  youtube: "youtube",
  reddit: "reddit",
  attribuly: "attribuly",
  snapchat_ads: "snapchat-ads",
  figma: "figma",
};

export const INTEGRATION_PROVIDER_ALIASES: Readonly<Record<string, string>> = {
  "adobe-commerce": "magento",
  "presta-shop": "prestashop",
  googleads: "google-ads",
  klaviyo: "klaviyo-oauth",
  "mailchimp-oauth": "mailchimp",
  "similar-web": "similarweb",
  "insider-one-oauth": "insider-one",
  insider: "insider-one",
  useinsider: "insider-one",
  "emarsys-oauth": "emarsys",
  "sap-emarsys": "emarsys",
  "sap-engagement-cloud": "emarsys",
  "figma-oauth": "figma",
  "figma-api-key": "figma",
  "go-aff-pro": "goaffpro",
  "up-promote": "uppromote",
  "loox-reviews": "loox",
  "trustwill-loyalty": "trustwill",
  loloyal: "trustwill",
  "lo-loyal": "trustwill",
  "nox-influencer": "noxinfluencer",
  nox: "noxinfluencer",
  "judgeme-oauth": "judgeme",
  "judge-me": "judgeme",
  "judge.me": "judgeme",
  judge: "judgeme",
  metaads: "meta-ads",
  facebook: "facebook-page",
  linkedinads: "linkedin-ads",
  linkedin: "linkedin-ads",
  pinterestads: "pinterest-ads",
  pinterest: "pinterest-ads",
  xads: "x-ads",
  "twitter-ads": "x-ads",
  twitterads: "x-ads",
  "twitter-x": "x-ads",
  twitterx: "x-ads",
  twitter: "x-ads",
  x: "x-ads",
  "youtube-ads": "youtube",
  youtubeads: "youtube",
  "reddit-social": "reddit",
  "attribuly-attribution": "attribuly",
  snapchatads: "snapchat-ads",
  snapchat: "snapchat-ads",
};

export const INTEGRATION_CATALOG_GROUPS: readonly IntegrationCatalogGroup[] = [
  {
    category: "E-commerce Platforms",
    tools: [
      "Shopify",
      "WooCommerce",
      "BigCommerce",
      "Magento",
      "Wix",
      "Squarespace",
      "PrestaShop",
      "Salesforce Commerce",
    ],
  },
  {
    category: "POS Systems",
    tools: [
      "Square",
      "Toast",
      "Clover",
      "Lightspeed",
      "Shopify POS",
      "Vend",
      "Revel",
      "TouchBistro",
    ],
  },
  {
    category: "Advertising",
    tools: [
      "Meta Ads",
      "Google Ads",
      "TikTok Ads",
      "Snapchat Ads",
      "Pinterest Ads",
      "LinkedIn Ads",
      "X Ads",
    ],
  },
  {
    category: "Social Media",
    tools: ["Facebook Page", "Instagram", "YouTube", "Reddit"],
  },
  {
    category: "Reviews",
    tools: ["Judge.me", "Okendo", "Loox"],
  },
  {
    category: "Affiliate Marketing",
    tools: ["GoAffPro", "UpPromote"],
  },
  {
    category: "Influencer Marketing",
    tools: ["NoxInfluencer"],
  },
  {
    category: "Loyalty",
    tools: ["TrustWILL"],
  },
  {
    category: "Email Marketing",
    tools: [
      "Klaviyo",
      "Mailchimp",
      "Insider One",
      "Emarsys",
      "Attentive",
      "Postscript",
      "Omnisend",
      "Sendlane",
      "ActiveCampaign",
      "Constant Contact",
    ],
  },
  {
    category: "Analytics",
    tools: [
      "Google Analytics",
      "Attribuly",
      "Segment",
      "Mixpanel",
      "Amplitude",
      "Heap",
      "Hotjar",
      "Looker",
      "Tableau",
    ],
  },
  {
    category: "SEO",
    tools: [
      "Google Search Console",
      "Semrush",
      "Similarweb",
      "Ahrefs",
    ],
  },
  {
    category: "Payments & Accounting",
    tools: [
      "Stripe",
      "PayPal",
      "QuickBooks",
      "Xero",
      "Square Payments",
      "Braintree",
      "FreshBooks",
      "Wave",
    ],
  },
  {
    category: "Customer Support & CRM",
    tools: [
      "Zendesk",
      "Intercom",
      "HubSpot",
      "Salesforce",
      "Gorgias",
      "Freshdesk",
      "Help Scout",
      "Pipedrive",
    ],
  },
  {
    category: "Shipping & Fulfillment",
    tools: [
      "ShipStation",
      "ShipBob",
      "Deliverr",
      "EasyShip",
      "UPS",
      "FedEx",
      "USPS",
      "DHL",
    ],
  },
  {
    category: "MCP",
    tools: [MCP_TOOL_NAME],
  },
  {
    category: "Team Collaboration",
    tools: ["Slack", "Feishu", "DingTalk"],
  },
  {
    category: "Design & Collaboration",
    tools: ["Figma"],
  },
];

export const INTEGRATION_SOURCE_CATEGORY_TO_SCENARIO: Readonly<
  Partial<Record<string, IntegrationBrowseScenario>>
> = {
  "E-commerce Platforms": "Store Ops",
  "POS Systems": "Store Ops",
  Advertising: "Advertising",
  "Email Marketing": "Email Marketing",
  Analytics: "Analytics",
  SEO: "SEO",
  "Social Media": "Social Media",
  Reviews: "Customers",
  "Affiliate Marketing": "Marketing",
  "Influencer Marketing": "Marketing",
  Loyalty: "Customers",
  "Customer Support & CRM": "Customers",
  "Payments & Accounting": "Finance",
  MCP: "MCP",
  "Team Collaboration": "Team Collaboration",
  "Design & Collaboration": "Team Collaboration",
};

export const getIntegrationBrowseDisplayScenario = (
  scenario: IntegrationBrowseScenario,
): IntegrationBrowseDisplayScenario =>
  scenario === "Email Marketing" ? "Marketing" : scenario;

const SUPPORTED_INTEGRATION_APP_SET = new Set(SUPPORTED_INTEGRATION_APPS);

const normalizeIntegrationToolKey = (tool: string) =>
  tool
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");

export const getIntegrationProviderForTool = (tool: string): string | null =>
  INTEGRATION_TOOL_TO_PROVIDER[normalizeIntegrationToolKey(tool)] || null;

export const normalizeIntegrationProvider = (value?: string | null): string => {
  const normalized = (value || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-");
  return INTEGRATION_PROVIDER_ALIASES[normalized] || normalized;
};

export const isConnectableIntegrationTool = (tool: string): boolean => {
  const toolKey = normalizeIntegrationToolKey(tool);
  const provider = getIntegrationProviderForTool(tool);

  return (
    SUPPORTED_INTEGRATION_APP_SET.has(toolKey) ||
    Boolean(
      provider &&
      SUPPORTED_INTEGRATION_APP_SET.has(provider.replace(/-/g, "_")),
    )
  );
};

export const getIntegrationCatalogGroups = (
  includeMcp: boolean = true,
): IntegrationCatalogGroup[] =>
  INTEGRATION_CATALOG_GROUPS.filter(
    (group) => includeMcp || group.category !== "MCP",
  ).map((group) => ({ category: group.category, tools: [...group.tools] }));

export const getIntegrationCatalogTools = (
  includeMcp: boolean = true,
): string[] =>
  getIntegrationCatalogGroups(includeMcp)
    .flatMap((group) => group.tools)
    .filter((tool, index, tools) => tools.indexOf(tool) === index);

const INTEGRATION_CATALOG_DISPLAY_NAME_BY_KEY = new Map<string, string>();
getIntegrationCatalogTools().forEach((tool) => {
  INTEGRATION_CATALOG_DISPLAY_NAME_BY_KEY.set(
    normalizeIntegrationProvider(tool),
    tool,
  );
  const provider = getIntegrationProviderForTool(tool);
  if (provider) {
    INTEGRATION_CATALOG_DISPLAY_NAME_BY_KEY.set(
      normalizeIntegrationProvider(provider),
      tool,
    );
  }
});

export const getIntegrationCatalogDisplayName = (
  value?: string | null,
): string => {
  const sourceValue = String(value || "").trim();
  if (!sourceValue) return "";

  return (
    INTEGRATION_CATALOG_DISPLAY_NAME_BY_KEY.get(
      normalizeIntegrationProvider(sourceValue),
    ) || sourceValue
  );
};

export const getConnectableIntegrationToolsForScenario = (
  scenario: ConnectableIntegrationScenario,
): string[] => {
  const seenTools = new Set<string>();

  return INTEGRATION_CATALOG_GROUPS.flatMap((group) => {
    if (INTEGRATION_SOURCE_CATEGORY_TO_SCENARIO[group.category] !== scenario) {
      return [];
    }

    return group.tools.filter((tool) => {
      if (seenTools.has(tool) || !isConnectableIntegrationTool(tool)) {
        return false;
      }

      seenTools.add(tool);
      return true;
    });
  });
};

/**
 * Returns the real, connectable tools for the category labels shown in the
 * Admin browse navigation. In particular, the visible Marketing category
 * intentionally combines Email Marketing with the other marketing groups.
 */
export const getConnectableIntegrationToolsForBrowseScenario = (
  scenario: IntegrationBrowseDisplayScenario,
): string[] => {
  const seenTools = new Set<string>();

  return INTEGRATION_CATALOG_GROUPS.flatMap((group) => {
    const sourceScenario = INTEGRATION_SOURCE_CATEGORY_TO_SCENARIO[group.category];
    if (!sourceScenario || getIntegrationBrowseDisplayScenario(sourceScenario) !== scenario) {
      return [];
    }

    return group.tools.filter((tool) => {
      if (seenTools.has(tool) || !isConnectableIntegrationTool(tool)) {
        return false;
      }

      seenTools.add(tool);
      return true;
    });
  });
};
